import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { adminAPI, companiesAPI, affiliateCompaniesAPI, authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Users, Loader2, Shield, ShieldCheck, Trash2, Save, Building2, Briefcase, Plus, KeyRound } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const PAGE_LABELS = {
  dashboard: 'Dashboard', companies: 'Companies', partners: 'Partners',
  affiliate: 'Affiliate', incomes: 'Incomes', expenses: 'Expenses',
  personnel: 'Personnel', subscriptions: 'Subscriptions', wallets: 'Wallets',
  transactions: 'Transactions', reports: 'Reports', update: 'Update', settings: 'Settings',
};
const ALL_PAGES = Object.keys(PAGE_LABELS);

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [affiliateCompanies, setAffiliateCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [edited, setEdited] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', password: '' });
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes, affRes] = await Promise.all([
        adminAPI.getUsers(), companiesAPI.getAll(), affiliateCompaniesAPI.getAll()
      ]);
      setUsers(usersRes.data); setCompanies(companiesRes.data); setAffiliateCompanies(affRes.data);
    } catch (e) { toast.error(e.response?.status === 403 ? 'Admin access required' : 'Failed to load'); }
    finally { setLoading(false); }
  };

  const getEdited = (userId, field) => edited[userId]?.[field];
  const getUserField = (user, field) => getEdited(user.id, field) ?? user[field] ?? [];
  const hasChanges = (userId) => edited[userId] !== undefined;

  const setField = (userId, field, value) => {
    setEdited(prev => ({ ...prev, [userId]: { ...(prev[userId] || {}), [field]: value } }));
  };

  const toggleItem = (userId, field, item) => {
    const current = getUserField(users.find(u => u.id === userId), field);
    const updated = current.includes(item) ? current.filter(i => i !== item) : [...current, item];
    setField(userId, field, updated);
  };

  const savePermissions = async (userId) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const data = edited[userId] || {};
      const user = users.find(u => u.id === userId);
      await adminAPI.updatePermissions(userId, {
        allowed_pages: data.allowed_pages ?? user.allowed_pages ?? [],
        allowed_companies: data.allowed_companies ?? user.allowed_companies ?? [],
        allowed_affiliate_companies: data.allowed_affiliate_companies ?? user.allowed_affiliate_companies ?? [],
      });
      toast.success('Permissions saved');
      fetchData();
      setEdited(prev => { const n = { ...prev }; delete n[userId]; return n; });
    } catch { toast.error('Failed to save'); }
    finally { setSaving(prev => ({ ...prev, [userId]: false })); }
  };

  const handleDelete = async (userId) => {
    try { await adminAPI.deleteUser(userId); toast.success('User deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault(); setAddingUser(true);
    try {
      await authAPI.register(newUser);
      toast.success('User created');
      setAddDialogOpen(false); setNewUser({ full_name: '', email: '', password: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    finally { setAddingUser(false); }
  };

  const handleReset2FA = async (userId) => {
    try { await adminAPI.reset2FA(userId); toast.success('2FA reset'); fetchData(); }
    catch { toast.error('Failed to reset 2FA'); }
  };

  if (loading) { return (<Layout title="Users"><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>); }

  return (
    <Layout title="Users">
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div><h2 className="font-heading text-2xl font-bold">User Management</h2><p className="text-muted-foreground">Manage user access, page and company permissions</p></div>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild><Button className="rounded-full"><Plus className="h-4 w-4 mr-2" />Add User</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Create New User</DialogTitle></DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="space-y-2"><Label>Full Name *</Label><Input value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} placeholder="John Doe" required /></div>
                <div className="space-y-2"><Label>Email *</Label><Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" required /></div>
                <div className="space-y-2"><Label>Password *</Label><Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 characters" required /></div>
                <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={addingUser}>{addingUser && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {users.map(user => {
          const isAdmin = user.role === 'admin';
          const changed = hasChanges(user.id);

          return (
            <Card key={user.id} className={changed ? 'border-primary/50' : ''} data-testid={`user-card-${user.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isAdmin ? 'bg-amber-500/10' : 'bg-primary/10'}`}>
                      {isAdmin ? <ShieldCheck className="h-5 w-5 text-amber-500" /> : <Users className="h-5 w-5 text-primary" />}
                    </div>
                    <div>
                      <CardTitle className="font-heading text-base">{user.full_name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={isAdmin ? 'default' : 'secondary'} className={isAdmin ? 'bg-amber-500 text-white' : ''}>{isAdmin ? 'Admin' : 'User'}</Badge>
                    {!isAdmin && (
                      <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete User</AlertDialogTitle><AlertDialogDescription>Delete "{user.full_name}"?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(user.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                      </AlertDialog>
                    )}
                    {!isAdmin && user.totp_enabled && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-amber-500" onClick={() => handleReset2FA(user.id)} title="Reset 2FA"><KeyRound className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isAdmin ? (
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm text-amber-500 flex items-center gap-2"><Shield className="h-4 w-4" />Admin has full access</div>
                ) : (
                  <Tabs defaultValue="pages" className="space-y-3">
                    <TabsList className="h-8">
                      <TabsTrigger value="pages" className="text-xs h-7">Pages</TabsTrigger>
                      <TabsTrigger value="companies" className="text-xs h-7">Companies</TabsTrigger>
                      <TabsTrigger value="affiliates" className="text-xs h-7">Affiliates</TabsTrigger>
                    </TabsList>

                    <TabsContent value="pages">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Page Access</span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_pages', [...ALL_PAGES])}>All</Button>
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_pages', [])}>None</Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {ALL_PAGES.map(page => (
                            <div key={page} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                              <span className="text-xs">{PAGE_LABELS[page]}</span>
                              <Switch checked={getUserField(user, 'allowed_pages').includes(page)} onCheckedChange={() => toggleItem(user.id, 'allowed_pages', page)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="companies">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium flex items-center gap-1.5"><Building2 className="h-4 w-4" />Company Access</span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_companies', companies.map(c => c.id))}>All</Button>
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_companies', [])}>None</Button>
                          </div>
                        </div>
                        {companies.length > 0 ? (
                          <div className="grid grid-cols-2 gap-1.5">
                            {companies.map(c => (
                              <div key={c.id} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                                <span className="text-xs">{c.name}</span>
                                <Switch checked={getUserField(user, 'allowed_companies').includes(c.id)} onCheckedChange={() => toggleItem(user.id, 'allowed_companies', c.id)} />
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-muted-foreground">No companies created yet</p>}
                      </div>
                    </TabsContent>

                    <TabsContent value="affiliates">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium flex items-center gap-1.5"><Briefcase className="h-4 w-4" />Affiliate Company Access</span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_affiliate_companies', affiliateCompanies.map(c => c.id))}>All</Button>
                            <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setField(user.id, 'allowed_affiliate_companies', [])}>None</Button>
                          </div>
                        </div>
                        {affiliateCompanies.length > 0 ? (
                          <div className="grid grid-cols-2 gap-1.5">
                            {affiliateCompanies.map(c => (
                              <div key={c.id} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                                <span className="text-xs">{c.name}</span>
                                <Switch checked={getUserField(user, 'allowed_affiliate_companies').includes(c.id)} onCheckedChange={() => toggleItem(user.id, 'allowed_affiliate_companies', c.id)} />
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-muted-foreground">No affiliate companies yet</p>}
                      </div>
                    </TabsContent>
                  </Tabs>
                )}

                {changed && !isAdmin && (
                  <Button className="w-full mt-3" size="sm" onClick={() => savePermissions(user.id)} disabled={saving[user.id]}>
                    {saving[user.id] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Permissions
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}

        {users.filter(u => u.role !== 'admin').length === 0 && (
          <Card className="p-8"><div className="flex flex-col items-center justify-center text-center"><Users className="h-12 w-12 text-muted-foreground/50 mb-3" /><h3 className="font-heading font-semibold mb-1">No users yet</h3><p className="text-sm text-muted-foreground">Users will appear here when they register</p></div></Card>
        )}
      </div>
    </Layout>
  );
}
